import { useMemo } from "react";

import { Dialog } from "@ui/components/Dialog";
import { TranslationHtmlWrapper } from "@ui/components/TranslationHtmlWrapper/TranslationHtmlWrapper";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import { formatDate } from "@ui/utils/formatDate/formatDate";
import { QRCodeCanvas } from "qrcode.react";

import { useConfig } from "@jti/app-context/useConfig";
import { ProfileFamilyIcon } from "@jti/brand-icons/profile-family";
import { StoreDefaultIcon } from "@jti/brand-icons/store-default";
import type { ContactPersonal } from "@jti/fe-common";
import { formatFullName, ISO_DATE_FORMAT } from "@jti/fe-common";

import styles from "./SharableProfileDialog.module.scss";

type SharableProfileDialogProps = {
  isOpened: boolean;
  userInfo: ContactPersonal;
  userId: string;
  referralCode: string;
  onClose: () => void;
  portalDomNode?: HTMLElement;
};

export const SharableProfileDialog: React.FC<SharableProfileDialogProps> = ({
  isOpened,
  userInfo,
  userId,
  referralCode,
  onClose,
  portalDomNode,
}) => {
  const { __ } = useTranslations();
  const { isReversedNameOrder } = useConfig();
  const { breakpoint } = useDeviceBreakpoint();
  const isMobile = breakpoint === "mobile";
  const fullName = formatFullName(userInfo.firstname, userInfo.lastname, isReversedNameOrder);

  const userJsonForQr = useMemo(
    () =>
      JSON.stringify({
        uuid: userId,
        name: fullName,
        dob: formatDate(userInfo.dob, ISO_DATE_FORMAT),
        email: userInfo.email,
        code: referralCode,
      }),
    [userId, fullName, userInfo.dob, userInfo.email, referralCode]
  );

  return (
    <Dialog
      dataTestId="sharableProfileDialog"
      isOpened={isOpened}
      onClose={onClose}
      portalDomNode={portalDomNode}
      withContentPadding={false}
      fullscreen={isMobile}
    >
      <div className={styles.container}>
        <h2 className={styles.title} data-testid="sharableProfileTitle">
          {__("profile.personal_details.sharable_profile_title")}
        </h2>
        <div className={styles.qrInfoWrapper}>
          <div className={styles.qrCodeWrapper}>
            <p className={styles.fullName}>{fullName}</p>
            <QRCodeCanvas value={userJsonForQr} size={200} />
          </div>
          <div className={styles.howItWorksWrapper}>
            <p className={styles.howItWorksTitle}>
              {__("profile.personal_details.sharable_profile_instructions_title")}
            </p>
            <div className={styles.section}>
              <div className={styles.iconContainer}>
                <ProfileFamilyIcon data-testid="employee-icon" />
              </div>
              <p className={styles.text}>{__("profile.personal_details.sharable_profile_employee_instruction")}</p>
            </div>
            <div className={styles.section}>
              <div className={styles.iconContainer}>
                <StoreDefaultIcon data-testid="store-icon" />
              </div>
              <p className={styles.text}>{__("profile.personal_details.sharable_profile_store_instruction")}</p>
            </div>
            <TranslationHtmlWrapper
              className={styles.knowMoreLink}
              text="profile.personal_details.sharable_profile_know_more"
              htmlData={[]}
            />
          </div>
        </div>
        <p className={styles.agreementText}>{__("profile.personal_details.sharable_profile_agreement_notification")}</p>
      </div>
    </Dialog>
  );
};
